import { MenuOutlined } from '@ant-design/icons';
import { App, Layout, Menu } from 'antd';
import { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Link, useLocation } from 'react-router-dom';

import styles from './HeaderView.module.css';
import logoImage from './logo.svg';

const HeaderView = ({ isAdmin, isAuthenticated, onLogout, userData }) => {
    const { modal } = App.useApp();
    const location = useLocation();
    const [showCompactMenu, setShowCompactMenu] = useState(false);
    const isCompact = useMediaQuery({ maxWidth: 1168 });
    const subMenuClassName = isCompact ? null : styles.right;

    useEffect(() => {
        setShowCompactMenu(false);
    }, [location]);

    useEffect(() => {
        if (userData && userData.isBanned) {
            const warningModal = modal.warning();
            updateModalText(warningModal, 7);
            setTimeout(() => {
                onLogout();
            }, 7000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData]);

    const handleToggleCompactMenu = () => setShowCompactMenu(!showCompactMenu);

    const updateModalText = (warningModal, count) => {
        if (count >= 1) {
            warningModal.update({
                title: 'Warning',
                okButtonProps: { className: 'hideBannedOkBtn' },
                content: `You are blocked by Admin. We are logging you out of the system. Please contact through email for further details! (${count})`,
            });
            setTimeout(() => {
                updateModalText(warningModal, count - 1);
            }, 1000);
        }
    };

    // Admin menu items
    const adminMenuItems =
        isAuthenticated && isAdmin
            ? [
                  {
                      key: 'admin',
                      label: 'Admin',
                      className: subMenuClassName,
                      popupClassName: styles.popupContainer,
                      popupOffset: [-20, 0],
                      children: [
                          {
                              key: 'pending-sos',
                              label: <Link to="/sos/pending">Pending SOS Requests</Link>,
                          },
                          {
                              key: 'rejected-sos',
                              label: <Link to="/sos/rejected">Rejected SOS Requests</Link>,
                          },
                          {
                              key: 'pending-marketplace',
                              label: <Link to="/marketplace/pending">Pending Marketplace Items</Link>,
                          },

                          {
                              key: 'product-enquiries',
                              label: <Link to="/admin/product-enquiries">Product Enquiries Statistics</Link>,
                          },

                          {
                              key: 'users-list',
                              label: <Link to="/admin/users-list">Users List</Link>,
                          },
                          {
                              key: 'user-statistics',
                              label: <Link to="/admin/users">User Statistics</Link>,
                          },
                          {
                              key: 'user-sos-statistics',
                              label: <Link to="/admin/user-sos-statestics">User SOS Statistics</Link>,
                          },
                          {
                              key: 'sos-statistics',
                              label: <Link to="/admin/sos">SOS Statistics</Link>,
                          },
                      ],
                  },
              ]
            : [];

    // User menu items
    const userMenuItem = isAuthenticated
        ? {
              key: 'manage-account',
              label: 'Manage Account',
              className: subMenuClassName,
              popupClassName: styles.popupContainer,
              popupOffset: [-20, 0],
              children: [
                  {
                      key: 'my-profile',
                      label: <Link to="/profile">My Profile</Link>,
                  },
                  {
                      key: 'my-items',
                      label: <Link to="/marketplace/my-items">My Items</Link>,
                  },
                  {
                      key: 'my-sos',
                      label: <Link to="/sos/my-sos">My SOS</Link>,
                  },
                  {
                      key: 'logout',
                      label: 'Log Out',
                      onClick: onLogout,
                  },
              ],
          }
        : {
              key: 'signin',
              label: <Link to="/login">Sign in / Register</Link>,
              className: styles.right,
          };

    const DonateButton = (
        <a className={`${styles.donateButtonLink}`} href="https://www.paypal.com/donate/?hosted_button_id=36GXB2BW2YP2U" rel="noreferrer" target="_Blank">
            Donate
        </a>
    );

    // Common menu items
    const commonMenuItems = [
        {
            key: 'sos',
            label: <Link to="/sos/new">SOS</Link>,
            className: styles.sos,
        },
        {
            key: 'marketplace',
            label: <Link to="/marketplace">Marketplace</Link>,
            className: styles.right,
        },
        {
            key: 'support-us',
            label: <a href="https://www.skiggle.co.uk/support-us">Support Us</a>,
            className: styles.right,
        },
        {
            key: 'what-we-do',
            label: <a href="https://www.skiggle.co.uk/what-we-do">What We Do</a>,
            className: styles.right,
        },
        {
            key: 'about-us',
            label: 'About Us',
            className: subMenuClassName,
            popupClassName: styles.popupContainer,
            popupOffset: [-20, 0],
            children: [
                {
                    key: 'our-story',
                    label: <a href="https://www.skiggle.co.uk/about-us/our-story">Our Story</a>,
                },
                {
                    key: 'meet-the-team',
                    label: <a href="https://www.skiggle.co.uk/about-us/meet-the-team">Meet The Team</a>,
                },
                {
                    key: 'faqs',
                    label: <a href="https://www.skiggle.co.uk/about-us/faqs">FAQs</a>,
                },
                {
                    key: 'blog',
                    label: <a href="https://www.skiggle.co.uk/blog">Blog</a>,
                },
                {
                    key: 'member-reviews',
                    label: <a href="https://www.skiggle.co.uk/member-reviews">Member Reviews</a>,
                },
            ],
        },
    ];

    // Main menu items
    const mainMenuItems = [
        {
            key: 'logo',
            label: (
                <a href="https://www.skiggle.co.uk">
                    <img alt="Logo" className={styles.logo} src={logoImage} />
                </a>
            ),
            className: styles.logoMenuItem,
        },
    ];

    // Add toggle menu for compact view
    if (isCompact) {
        mainMenuItems.push({
            key: 'toggle-menu',
            label: null,
            icon: <MenuOutlined />,
            onClick: handleToggleCompactMenu,
            className: styles.right,
        });
    } else {
        // Add regular menu items for non-compact view
        mainMenuItems.push(userMenuItem);
        mainMenuItems.push(...adminMenuItems);
        mainMenuItems.push(...commonMenuItems);
        mainMenuItems.push({
            key: 'donate',
            label: DonateButton,
            className: styles.donateButtonContainer,
        });
    }

    // Compact menu items (shown when menu is expanded)
    const compactMenuItems = [userMenuItem, ...adminMenuItems, ...commonMenuItems];

    return (
        <Layout.Header className={styles.container}>
            <Menu className={styles.antMenuOverflow} mode="horizontal" theme="dark" items={mainMenuItems} />
            {isCompact ? <div className={styles.floatingDonateButton}>{DonateButton}</div> : ''}
            {isCompact && <Menu hidden={!showCompactMenu} mode="inline" theme="dark" items={compactMenuItems} />}
        </Layout.Header>
    );
};

export { HeaderView };
